import {
  EMPTY_STREAM_FILTERS,
  FACET_KEYS,
  applyStreamFilters as applyFilters,
  chosenFieldFilters,
  chosenOf,
  fieldFilterActive,
  streamFieldLabels,
  streamFieldValue,
  facetCounts as countFacets,
  streamFiltersSchema,
  type FacetKey,
  type MediaType,
  type BuiltinColumnId,
  type ColumnId,
  type StreamFieldFilter,
  type StreamFilters,
  type StreamHit,
  type StreamSearch,
  type StreamSentiment,
} from '@headline/schemas'

/**
 * The stream's row vocabulary and filter logic live in @headline/schemas (one spelling for the
 * API and the pages); this module is the pages' binding of them plus the display helpers. The
 * relative date rules anchor on a moment captured once per load, never `Date.now()` in render,
 * so renders stay pure and a page open for an hour does not drift row by row.
 */
export {
  EMPTY_STREAM_FILTERS,
  FACET_KEYS,
  MEDIA_TYPE_LABELS,
  facetOptions,
  streamSearchSchema,
  type FacetKey,
  type MediaType,
  type StreamFilters,
  type StreamHit,
  type StreamSearch,
  type StreamSentiment,
} from '@headline/schemas'

/** The pages' time anchor — captured once at module load. */
export const STREAM_NOW = Date.now()

/** The Library column each fixed facet filters — a facet shows, and filters, only while its column
 *  is in the workspace's layout. */
const FACET_COLUMNS: Record<FacetKey, BuiltinColumnId> = {
  mediaTypes: 'mediaType',
  folders: 'folder',
  topics: 'topic',
  tags: 'tag',
  outlets: 'outlet',
  sentiments: 'sentiment',
  classifications: 'classification',
}

/** The fixed facets whose columns the layout shows, in the filter bar's order. */
export const shownFacets = (columns: readonly ColumnId[]): FacetKey[] =>
  FACET_KEYS.filter((key) => columns.includes(FACET_COLUMNS[key]))

/**
 * The filters a page applies under the workspace's layout (CLI-1009): a filter counts only while
 * its column is shown — a fixed facet's, or a build column's (`chosenFieldFilters`). One carried in
 * from the other page or kept by a saved view waits for its column to come back.
 */
export function withLayoutFilters(
  filters: StreamFilters,
  columns: readonly ColumnId[],
): StreamFilters {
  const hidden = FACET_KEYS.filter((key) => !columns.includes(FACET_COLUMNS[key]))
  const { fields: _all, ...rest } = filters
  const next: StreamFilters = { ...rest, ...Object.fromEntries(hidden.map((key) => [key, []])) }
  const fields = chosenFieldFilters(filters.fields, chosenOf(columns))
  return fields ? { ...next, fields } : next
}

/** The filters with one build column's filter set, or dropped (null) — an empty one is dropped
 *  too, so a selection without column filters never carries an empty `fields`. */
export function withFieldFilter(
  filters: StreamFilters,
  key: string,
  filter: StreamFieldFilter | null,
): StreamFilters {
  const { [key]: _old, ...others } = filters.fields ?? {}
  const fields = filter && fieldFilterActive(filter) ? { ...others, [key]: filter } : others
  const { fields: _fields, ...rest } = filters
  return Object.keys(fields).length > 0 ? { ...rest, fields } : rest
}

export function applyStreamFilters(hits: StreamHit[], filters: StreamFilters): StreamHit[] {
  return applyFilters(hits, filters, STREAM_NOW)
}

/** A build column's label counts, the way the fixed facets count theirs: with every other filter
 *  applied and the column's own skipped, so ticking a value never zeroes its siblings. */
export function fieldCounts(
  corpus: StreamHit[],
  filters: StreamFilters,
  key: string,
): Map<string, number> {
  const { [key]: _own, ...others } = filters.fields ?? {}
  const counts = new Map<string, number>()
  for (const hit of applyStreamFilters(corpus, { ...filters, fields: others })) {
    for (const label of streamFieldLabels(streamFieldValue(hit, key))) {
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
  }
  return counts
}

/** A build column's filter in words, for its applied chip. */
export function describeFieldFilter(filter: StreamFieldFilter): string {
  if ('values' in filter) return filter.values.join(', ')
  if ('contains' in filter) return `contains “${filter.contains.trim()}”`
  if (filter.min !== undefined && filter.max !== undefined) return `${filter.min}–${filter.max}`
  return filter.min !== undefined ? `≥ ${filter.min}` : `≤ ${filter.max}`
}

export function facetCounts(
  hits: StreamHit[],
  filters: StreamFilters,
  key: FacetKey,
): Map<string, number> {
  return countFacets(hits, filters, key, STREAM_NOW)
}

/** Badge variant per sentiment — the same status-color mapping the rest of the app uses. A row
 *  nothing scored reads neutral. */
export function sentimentVariant(
  sentiment: StreamSentiment | null,
): 'success' | 'destructive' | 'neutral' | 'warning' {
  switch (sentiment) {
    case 'positive':
      return 'success'
    case 'negative':
      return 'destructive'
    case 'mixed':
      return 'warning'
    default:
      return 'neutral'
  }
}

/** What a row shows where nothing scored it. */
export const UNSCORED = '—'

export function formatReach(reach: number): string {
  if (reach >= 1_000_000) return `${(reach / 1_000_000).toFixed(1)}M`
  if (reach >= 1_000) return `${Math.round(reach / 1_000)}K`
  return String(reach)
}

export function formatHitDate(iso: string): string {
  return new Date(Date.parse(iso)).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

// ── Carrying filters between Analytics and Library (CLI-826) ──────────────────────────────────

const FACET_FIELDS = [
  'mediaTypes',
  'sentiments',
  'folders',
  'topics',
  'tags',
  'classifications',
  'outlets',
] as const

/** A chart's own narrowing, laid over the page's filters on a drill-through. */
export interface DrillNarrow {
  sentiment?: StreamSentiment | undefined
  mediaType?: MediaType | undefined
  daysAgo?: StreamFilters['daysAgo']
}

/**
 * The page's filters, plus a chart's narrowing, as Library's URL search. The chart wins where they
 * overlap — a "Negative mentions" tile means negative, whatever sentiments the page had — and a
 * chart's relative window replaces the page's dates. Empty fields are left out so the URL stays
 * short and a plain link reads as "no narrowing".
 */
export function drillSearch(filters: StreamFilters, narrow: DrillNarrow = {}): StreamSearch {
  const next: StreamFilters = {
    ...filters,
    ...(narrow.sentiment && { sentiments: [narrow.sentiment] }),
    ...(narrow.mediaType && { mediaTypes: [narrow.mediaType] }),
  }
  if (narrow.daysAgo !== undefined) {
    next.daysAgo = narrow.daysAgo
    delete next.from
    delete next.to
  }
  const search: StreamSearch = {}
  for (const field of FACET_FIELDS) {
    const values = next[field]
    if (values.length > 0) Object.assign(search, { [field]: values })
  }
  if (next.daysAgo !== undefined) search.daysAgo = next.daysAgo
  if (next.from) search.from = next.from
  if (next.to) search.to = next.to
  if (next.groupSyndication) search.groupSyndication = true
  if (next.search?.trim()) search.search = next.search
  return search
}

/** Two filter sets are the same selection. A field one of them predates reads as its empty value,
 *  so a view saved before a filter existed never shows as edited. */
export function sameFilters(a: StreamFilters, b: StreamFilters): boolean {
  return (
    JSON.stringify({ ...EMPTY_STREAM_FILTERS, ...a }) ===
    JSON.stringify({ ...EMPTY_STREAM_FILTERS, ...b })
  )
}

/** Library's URL search as filters, or null when it narrows nothing (open the default view). */
export function filtersFromSearch(search: StreamSearch): StreamFilters | null {
  const filters: StreamFilters = { ...EMPTY_STREAM_FILTERS }
  let narrowed = false
  for (const field of FACET_FIELDS) {
    const values = search[field]
    if (values && values.length > 0) {
      Object.assign(filters, { [field]: values })
      narrowed = true
    }
  }
  // The pre-CLI-826 single-value param, still honoured for links already shared.
  if (search.sentiment && filters.sentiments.length === 0) {
    filters.sentiments = [search.sentiment]
    narrowed = true
  }
  if (search.from || search.to) {
    if (search.from) filters.from = search.from
    if (search.to) filters.to = search.to
    narrowed = true
  } else if (search.daysAgo !== undefined) {
    filters.daysAgo = search.daysAgo
    narrowed = true
  }
  if (search.groupSyndication) {
    filters.groupSyndication = true
    narrowed = true
  }
  if (search.search?.trim()) {
    filters.search = search.search
    narrowed = true
  }
  return narrowed ? filters : null
}

/**
 * The workspace's live filters, shared by Analytics and Library for the length of the browser tab
 * (sessionStorage, per workspace): switching pages keeps what you narrowed by, while a new tab
 * still opens on each page's default view. Session-scoped on purpose — a filter that outlived the
 * tab would quietly override a saved default view the next morning. Best effort: storage that
 * throws (private mode, blocked) just means nothing carries.
 */
const carryKey = (workspace: string) => `headline.stream.filters.v1:${workspace}`

export function readCarriedFilters(workspace: string): StreamFilters | null {
  try {
    const raw = window.sessionStorage.getItem(carryKey(workspace))
    if (!raw) return null
    const parsed = streamFiltersSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

export function writeCarriedFilters(workspace: string, filters: StreamFilters): void {
  try {
    window.sessionStorage.setItem(carryKey(workspace), JSON.stringify(filters))
  } catch {
    // Nothing carries; each page falls back to its default view.
  }
}
